import { useState, useEffect } from 'react';
import Joyride, { STATUS, ACTIONS, EVENTS } from 'react-joyride';
import { useTheme } from '../context/ThemeContext';

// Tour steps for different sections
export const tourSteps = {
  dashboard: [
    {
      target: '[data-testid="dashboard-sidebar"]',
      content: 'This is your navigation sidebar. Access all dashboards, AI features, and settings from here.',
      title: 'Navigation Sidebar',
      placement: 'right',
      disableBeacon: true,
    },
    {
      target: '[data-testid="command-center-link"], [data-tour="command-center"]',
      content: 'Command Center is your main dashboard with key performance metrics and real-time insights.',
      title: 'Command Center',
      placement: 'right',
    },
    {
      target: '[data-testid="ai-conversation-link"], [data-tour="ai-conversation"]',
      content: 'Chat with your data using natural language. Ask questions like "Show me top 10 stores" and get instant answers.',
      title: 'AI Conversation',
      placement: 'right',
    },
    {
      target: '[data-testid="brand-selector"], [data-tour="brand-selector"]',
      content: 'Switch between different brands if you have access to multiple. Each brand has its own isolated data.',
      title: 'Brand Selector',
      placement: 'bottom',
    },
    {
      target: '[data-testid="help-button"], [data-tour="help-button"]',
      content: 'Click here anytime to access the Help Center with detailed guides and FAQs.',
      title: 'Help & Support',
      placement: 'left',
    },
  ],
  
  commandCenter: [
    {
      target: '[data-testid="kpi-cards"], [data-tour="kpi-cards"]',
      content: 'These KPI cards show your most important metrics at a glance: members, revenue, transactions, and more.',
      title: 'Key Performance Indicators',
      placement: 'bottom',
      disableBeacon: true,
    },
    {
      target: '[data-testid="date-filter"], [data-tour="date-filter"]',
      content: 'Filter all dashboard data by date range. Select preset periods or custom dates.',
      title: 'Date Filter',
      placement: 'bottom',
    },
    {
      target: '[data-testid="store-filter"], [data-tour="store-filter"]',
      content: 'Filter by specific stores or cities to drill down into location-specific performance.',
      title: 'Store & City Filters',
      placement: 'bottom',
    },
    {
      target: '[data-testid="ai-insights-btn"], [data-tour="ai-insights"]',
      content: 'Click to generate AI-powered insights based on your current filters and data.',
      title: 'AI Insights',
      placement: 'left',
    },
    {
      target: '[data-testid="charts-section"], [data-tour="charts"]',
      content: 'Interactive charts visualize your data. Hover for details, click for drill-downs.',
      title: 'Data Visualizations',
      placement: 'top',
    },
  ],
  
  storePerformance: [
    {
      target: '[data-testid="store-search"], [data-tour="store-search"]',
      content: 'Search for specific stores by name or code to quickly find what you need.',
      title: 'Store Search',
      placement: 'bottom',
      disableBeacon: true,
    },
    {
      target: '[data-testid="store-table"], [data-tour="store-table"]',
      content: 'Click any row to drill down into detailed store analytics including daily trends and top customers.',
      title: 'Store Performance Table',
      placement: 'top',
    },
    {
      target: '[data-testid="city-filter"], [data-tour="city-filter"]',
      content: 'Filter stores by city to compare performance across different regions.',
      title: 'City Filter',
      placement: 'bottom',
    },
  ],
  
  aiConversation: [
    {
      target: '[data-testid="ai-chat"], [data-tour="ai-chat"]',
      content: 'Welcome to AI Conversation! Ask questions about your loyalty data in plain English.',
      title: 'AI Chat Interface',
      placement: 'center',
      disableBeacon: true,
    },
    {
      target: '[data-testid="suggested-prompts"], [data-tour="suggested-prompts"]',
      content: 'Click any suggested prompt to start a conversation. These are common questions to get you started.',
      title: 'Suggested Prompts',
      placement: 'top',
    },
    {
      target: '[data-testid="message-input"], [data-tour="message-input"]',
      content: 'Type your question here. Be specific - mention time periods, stores, or metrics for better results.',
      title: 'Message Input',
      placement: 'top',
    },
    {
      target: '[data-testid="chat-history"], [data-tour="chat-history"]',
      content: 'All your conversations are saved. Access previous chats from the history panel.',
      title: 'Chat History',
      placement: 'right',
    },
  ],
  
  campaignBuilder: [
    {
      target: '[data-testid="campaign-type"], [data-tour="campaign-type"]',
      content: 'Choose your campaign channel: SMS for text messages or WhatsApp for rich messaging.',
      title: 'Campaign Type',
      placement: 'bottom',
      disableBeacon: true,
    },
    {
      target: '[data-testid="audience-selector"], [data-tour="audience"]',
      content: 'Select your target audience. Filter by RFM segment, store, city, or create custom filters.',
      title: 'Audience Selection',
      placement: 'bottom',
    },
    {
      target: '[data-testid="message-composer"], [data-tour="message"]',
      content: 'Compose your message. Use merge tags like {name} and {points} to personalize.',
      title: 'Message Composer',
      placement: 'top',
    },
    {
      target: '[data-testid="schedule-options"], [data-tour="schedule"]',
      content: 'Send immediately or schedule for later. Set up recurring campaigns for automation.',
      title: 'Scheduling',
      placement: 'left',
    },
  ],
  
  rfmDashboard: [
    {
      target: '[data-testid="rfm-segments"], [data-tour="rfm-segments"]',
      content: 'Customer segments based on Recency, Frequency, and Monetary value. Each segment needs different strategies.',
      title: 'RFM Segments',
      placement: 'bottom',
      disableBeacon: true,
    },
    {
      target: '[data-testid="segment-champions"], [data-tour="champions"]',
      content: 'Champions are your best customers - recent, frequent, high spenders. Reward and retain them!',
      title: 'Champions Segment',
      placement: 'right',
    },
    {
      target: '[data-testid="segment-at-risk"], [data-tour="at-risk"]',
      content: 'At Risk customers were active but are slipping away. Target them with win-back campaigns.',
      title: 'At Risk Segment',
      placement: 'right',
    },
    {
      target: '[data-testid="churn-prediction"], [data-tour="churn"]',
      content: 'See which customers are likely to churn based on their behavior patterns.',
      title: 'Churn Prediction',
      placement: 'top',
    },
  ],
};

// Custom tooltip component
const CustomTooltip = ({
  continuous,
  index,
  step,
  backProps,
  closeProps,
  primaryProps,
  tooltipProps,
  isLastStep,
  size,
}) => {
  return (
    <div
      {...tooltipProps}
      className="bg-card border rounded-xl shadow-xl max-w-md p-0 overflow-hidden"
    >
      {/* Progress bar */}
      <div className="h-1 bg-muted">
        <div 
          className="h-full bg-primary transition-all duration-300"
          style={{ width: `${((index + 1) / size) * 100}%` }}
        />
      </div>
      
      <div className="p-4">
        {step.title && (
          <h4 className="font-semibold text-lg mb-2">{step.title}</h4>
        )}
        <p className="text-muted-foreground text-sm">{step.content}</p>
      </div>
      
      <div className="flex items-center justify-between p-4 pt-0 gap-2">
        <span className="text-xs text-muted-foreground">
          Step {index + 1} of {size}
        </span>
        <div className="flex gap-2">
          {index > 0 && (
            <button
              {...backProps}
              className="px-3 py-1.5 text-sm rounded-lg border hover:bg-muted transition-colors"
            >
              Back
            </button>
          )}
          {continuous && (
            <button
              {...primaryProps}
              className="px-3 py-1.5 text-sm rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              {isLastStep ? 'Finish' : 'Next'}
            </button>
          )}
          {!continuous && (
            <button
              {...closeProps}
              className="px-3 py-1.5 text-sm rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              Got it
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export function GuidedTour({ tourKey, run, onComplete }) {
  const { theme } = useTheme();
  const [stepIndex, setStepIndex] = useState(0);
  
  const steps = tourSteps[tourKey] || [];
  
  const handleJoyrideCallback = (data) => {
    const { status, action, index, type } = data;
    
    if (type === EVENTS.STEP_AFTER) {
      setStepIndex(index + 1);
    }
    
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      // Save completion to localStorage
      const completedTours = JSON.parse(localStorage.getItem('completedTours') || '{}');
      completedTours[tourKey] = true;
      localStorage.setItem('completedTours', JSON.stringify(completedTours));
      
      if (onComplete) onComplete();
    }
    
    if (action === ACTIONS.CLOSE) {
      if (onComplete) onComplete();
    }
  };
  
  if (!steps.length) return null;
  
  return (
    <Joyride
      steps={steps}
      run={run}
      continuous
      showProgress
      showSkipButton
      stepIndex={stepIndex}
      callback={handleJoyrideCallback}
      tooltipComponent={CustomTooltip}
      styles={{
        options: {
          arrowColor: theme === 'dark' ? '#1f2937' : '#ffffff',
          backgroundColor: theme === 'dark' ? '#1f2937' : '#ffffff',
          overlayColor: 'rgba(0, 0, 0, 0.5)',
          primaryColor: '#8B5CF6',
          textColor: theme === 'dark' ? '#f3f4f6' : '#1f2937',
          zIndex: 10000,
        },
        spotlight: {
          borderRadius: 8,
        },
      }}
      floaterProps={{
        disableAnimation: true,
      }}
      locale={{
        back: 'Back',
        close: 'Close',
        last: 'Finish',
        next: 'Next',
        skip: 'Skip tour',
      }}
    />
  );
}

// Hook to check if tour should auto-run
export function useTourStatus(tourKey) {
  const [hasCompleted, setHasCompleted] = useState(true);
  const [toursDisabled, setToursDisabled] = useState(false);
  
  useEffect(() => {
    const completedTours = JSON.parse(localStorage.getItem('completedTours') || '{}');
    const disabled = localStorage.getItem('toursDisabled') === 'true';
    setHasCompleted(!!completedTours[tourKey]);
    setToursDisabled(disabled);
  }, [tourKey]);
  
  const resetTour = () => {
    const completedTours = JSON.parse(localStorage.getItem('completedTours') || '{}');
    delete completedTours[tourKey];
    localStorage.setItem('completedTours', JSON.stringify(completedTours));
    setHasCompleted(false);
  };
  
  const disableAllTours = () => {
    localStorage.setItem('toursDisabled', 'true');
    setToursDisabled(true);
  };
  
  const enableAllTours = () => {
    localStorage.setItem('toursDisabled', 'false');
    setToursDisabled(false);
  };
  
  return { hasCompleted, toursDisabled, resetTour, disableAllTours, enableAllTours };
}

// Button to start tour manually
export function TourButton({ tourKey, onClick }) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg border hover:bg-muted transition-colors"
      data-testid={`start-tour-${tourKey}`}
    >
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      Take a tour
    </button>
  );
}

export default GuidedTour;
